import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { MoreHorizontal } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { deleteMember, promoteMember } from '@/services/workspace';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, } from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { errorHandler } from '@/lib/utils';
export const MemberActions = ({ member, workspace }) => {
    const { t } = useTranslation();
    const [openRoleDialog, setOpenRoleDialog] = React.useState(false);
    const [role, setRole] = React.useState(member.role);
    const { mutate } = useMutation({
        mutationFn: promoteMember,
        onSuccess: (data) => {
            toast.success(data.message);
            setOpenRoleDialog(false);
        },
        onError: errorHandler,
    });
    const { mutate: removeMember, isPending: isDeleting } = useMutation({
        mutationFn: deleteMember,
        onSuccess: (data) => {
            toast.success(data.message);
        },
        onError: errorHandler,
    });
    const onSubmit = React.useCallback(() => {
        mutate({ workspaceId: workspace.publicId, memberId: member.publicId, role });
    }, [mutate, workspace.publicId, member.publicId, role]);
    if (member.role === 'OWNER') {
        return <Separator />;
    }
    return (<DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4"/>
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                    <Dialog open={openRoleDialog} onOpenChange={(open) => {
            setRole(member.role);
            setOpenRoleDialog(open);
        }}>
                        <DialogTrigger asChild>
                            <Button variant="ghost" className="w-full">
                                {t('change_role')}
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>{t('change_role')}</DialogTitle>
                                <DialogDescription>{t('change_role_help')}</DialogDescription>
                            </DialogHeader>

                            <Select value={role} onValueChange={(role) => setRole(role)}>
                                <SelectTrigger>
                                    <SelectValue placeholder={t('select_role')}/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="ADMIN">{t('admin')}</SelectItem>
                                    <SelectItem value="MANAGER">{t('manager')}</SelectItem>
                                    <SelectItem value="MEMBER">{t('member')}</SelectItem>
                                </SelectContent>
                            </Select>

                            <DialogFooter className="sm:justify-start">
                                <DialogClose asChild>
                                    <Button type="button" variant="secondary">
                                        {t('close')}
                                    </Button>
                                </DialogClose>
                                <Button onClick={onSubmit}>{t('save')}</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button variant="ghost" className="text-destructive w-full">
                                {t('remove_member')}
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>{t('remove_member_confirm_title')}</AlertDialogTitle>

                                <AlertDialogDescription>
                                    {t('remove_member_confirm_description')}
                                </AlertDialogDescription>
                            </AlertDialogHeader>

                            <AlertDialogFooter>
                                <AlertDialogCancel disabled={isDeleting}>{t('cancel')}</AlertDialogCancel>

                                <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" disabled={isDeleting} onClick={() => {
            removeMember({ workspaceId: workspace.publicId, memberId: member.publicId });
        }}>
                                    {t('remove_member_confirm_button')}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>);
};
MemberActions.displayName = 'MemberActions';
